import { ReactNode, useEffect } from 'react';
import { useRouter } from 'src/routes/hooks';
import { paths } from 'src/routes/paths';

import { usePermissionCheck } from '../utils/permissions-utils';
import { useAuthStore } from '../store/auth.store';
import type { FeatureType } from '../types/permissions';

interface FeatureGuardProps {
  children: ReactNode;
  feature: FeatureType;
  fallbackPath?: string;
  showForbidden?: boolean;
  renderFallback?: () => ReactNode; // Componente personalizado quando não tem acesso
}

export default function FeatureGuard({
  children,
  feature,
  fallbackPath = paths.root,
  showForbidden = true,
  renderFallback,
}: FeatureGuardProps) {
  const router = useRouter();
  const { checkFeature } = usePermissionCheck();
  const { isAuthenticated } = useAuthStore();

  // Re-avalia permissões (token refresh é automático via interceptor)
  const hasFeatureAccess = checkFeature(feature);

  useEffect(() => {
    // Se não está autenticado, redireciona para login
    if (!isAuthenticated) {
      router.replace(paths.auth.login);
      return;
    }

    if (!hasFeatureAccess && !renderFallback) {
      if (showForbidden) {
        router.replace(paths.error.forbidden);
      } else {
        router.replace(fallbackPath);
      }
    }
  }, [hasFeatureAccess, renderFallback, showForbidden, router, fallbackPath, isAuthenticated]);

  if (!hasFeatureAccess) {
    if (renderFallback) {
      return <>{renderFallback()}</>;
    }

    return null;
  }

  return <>{children}</>;
}
